import Phaser from 'phaser';
import { assertNever } from '../../../common/helper';
import { GameAssetType, GameSpriteType } from '../game-types';

export type GameAsset = {
  type: GameAssetType;
  dimensions: {
    width: number;
    height: number;
  };
};

export type GameSprite = {
  type: GameSpriteType;
  dimensions: {
    frameWidth: number;
    frameHeight: number;
  };
};

export class AssetCatalogueService {
  private gameAssets = new Map<GameAssetType, GameAsset>();
  private gameSprites = new Map<GameSpriteType, GameSprite>();

  constructor() {
    this.buildAssetCatalogue();
    this.buildSpriteCatalogue();
  }

  public preloadCatalogue(scene: Phaser.Scene, localAssetPath: string) {
    for (const type of this.gameAssets.keys()) {
      scene.load.image(type.valueOf(), `${localAssetPath}/${type.valueOf()}.png`);
    }

    this.gameSprites.forEach((value, type) => {
      scene.load.spritesheet(type.valueOf(), `${localAssetPath}/${type.valueOf()}.png`, {
        frameWidth: value.dimensions.frameWidth,
        frameHeight: value.dimensions.frameHeight,
      });
    });
  }

  public getAsset(type: GameAssetType): GameAsset {
    const asset = this.gameAssets.get(type);
    if (asset === undefined) {
      throw new Error(`Asset undefined for type: ${type}`);
    }
    return asset;
  }

  public getSprite(type: GameSpriteType): GameSprite {
    const sprite = this.gameSprites.get(type);
    if (sprite === undefined) {
      throw new Error(`Sprite undefined for type: ${type}`);
    }
    return sprite;
  }

  private buildAssetCatalogue(): void {
    const addAsset = (type: GameAssetType, width: number, height: number) => {
      this.gameAssets.set(type, {
        type: type,
        dimensions: {
          width: width,
          height: height,
        },
      });
    };

    for (const [_key, value] of Object.entries(GameAssetType)) {
      switch (value) {
        case GameAssetType.PileFoundation:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.PileDeck:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.Pile:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.CardBack:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.CardClub:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.CardDiamond:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.CardHeart:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.CardSpade:
          addAsset(value, 256, 384);
          break;
        case GameAssetType.CardShadow:
          addAsset(value, 315, 443);
          break;
        case GameAssetType.StartButton:
          addAsset(value, 384, 384);
          break;
        default:
          assertNever(value, 'Undefined game asset');
      }
    }
  }

  private buildSpriteCatalogue(): void {
    const addSprite = (type: GameSpriteType, frameWidth: number, frameHeight: number) => {
      this.gameSprites.set(type, {
        type: type,
        dimensions: {
          frameWidth: frameWidth,
          frameHeight: frameHeight,
        },
      });
    };

    for (const [_key, value] of Object.entries(GameSpriteType)) {
      switch (value) {
        case GameSpriteType.NumbersLargePink:
          addSprite(value, 170, 195);
          break;
        case GameSpriteType.NumbersLargeBlack:
          addSprite(value, 170, 195);
          break;
        case GameSpriteType.NumbersSmallPink:
          addSprite(value, 68, 70);
          break;
        case GameSpriteType.NumbersSmallBlack:
          addSprite(value, 68, 70);
          break;
        default:
          assertNever(value, 'Undefined game sprite');
      }
    }
  }
}
